import { JobEnrichment } from "../types";

/**
 * Dynamically generates realistic and highly polished job quality enrichment details
 * for any given business or opportunity, using seed hashing for deterministic stability.
 * Supports comprehensive structured data fields for total compensation, workplace flexibility,
 * job security, career growth, company culture, and transparency metadata.
 */
export function generateJobEnrichment(lead: { name: string; id: string; rating?: number; category?: string }): JobEnrichment {
  const seedStr = (lead.name || "") + (lead.id || "");
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    hash = (hash << 5) - hash + seedStr.charCodeAt(i);
    hash |= 0;
  }
  const absHash = Math.abs(hash);

  // Determine pay scale and parameters based on business category
  const cat = (lead.category || "").toLowerCase();
  
  // Defaults for base pay and variables
  let basePay = "$18.50 - $24.00 / hr";
  let estimatedTotal = "$42,000 - $55,000 / yr";
  let bonusOrCommission = "No bonuses listed";
  let transparencyScore = 70;

  let basePayMin = 18.50;
  let basePayMax = 24.00;
  let payPeriod: "hour" | "week" | "month" | "year" = "hour";
  let currency = "USD";
  let estimatedAnnualMin = 37000;
  let estimatedAnnualMax = 48000;
  let bonusAvailable = false;
  let commissionAvailable = false;
  let overtimeAvailable = absHash % 3 === 0;

  // Let's refine based on category
  if (cat.includes("plumb") || cat.includes("electric") || cat.includes("hvac") || cat.includes("roof") || cat.includes("contract") || cat.includes("construction")) {
    basePayMin = 25.00 + (absHash % 10);
    basePayMax = 35.00 + (absHash % 15);
    basePay = `$${basePayMin.toFixed(2)} - $${basePayMax.toFixed(2)} / hr`;
    estimatedAnnualMin = Math.round(basePayMin * 2000);
    estimatedAnnualMax = Math.round(basePayMax * 2000);
    estimatedTotal = `$${estimatedAnnualMin.toLocaleString()} - $${estimatedAnnualMax.toLocaleString()} / yr`;
    bonusOrCommission = `+$2,500 performance sign-on bonus`;
    bonusAvailable = true;
    transparencyScore = 90;
  } else if (cat.includes("marketing") || cat.includes("consult") || cat.includes("legal") || cat.includes("account") || cat.includes("software") || cat.includes("tech")) {
    basePayMin = 30.00 + (absHash % 15);
    basePayMax = 50.00 + (absHash % 25);
    basePay = `$${basePayMin.toFixed(2)} - $${basePayMax.toFixed(2)} / hr`;
    estimatedAnnualMin = Math.round(basePayMin * 2000);
    estimatedAnnualMax = Math.round(basePayMax * 2000);
    estimatedTotal = `$${estimatedAnnualMin.toLocaleString()} - $${estimatedAnnualMax.toLocaleString()} / yr`;
    bonusOrCommission = `Performance-based commissions & profit sharing`;
    commissionAvailable = true;
    bonusAvailable = true;
    transparencyScore = 85;
  } else if (cat.includes("restaurant") || cat.includes("cafe") || cat.includes("food") || cat.includes("bakery") || cat.includes("bar")) {
    basePayMin = 16.50 + (absHash % 4);
    basePayMax = 21.00 + (absHash % 6);
    basePay = `$${basePayMin.toFixed(2)} - $${basePayMax.toFixed(2)} / hr`;
    estimatedAnnualMin = Math.round(basePayMin * 2000);
    estimatedAnnualMax = Math.round(basePayMax * 2000);
    estimatedTotal = `$${estimatedAnnualMin.toLocaleString()} - $${estimatedAnnualMax.toLocaleString()} / yr`;
    bonusOrCommission = `Cash tips distributed daily + shift meal credit`;
    bonusAvailable = true;
    transparencyScore = 95;
  } else if (cat.includes("dental") || cat.includes("health") || cat.includes("clinic") || cat.includes("care") || cat.includes("medical")) {
    basePayMin = 28.00 + (absHash % 12);
    basePayMax = 45.00 + (absHash % 20);
    basePay = `$${basePayMin.toFixed(2)} - $${basePayMax.toFixed(2)} / hr`;
    estimatedAnnualMin = Math.round(basePayMin * 2000);
    estimatedAnnualMax = Math.round(basePayMax * 2000);
    estimatedTotal = `$${estimatedAnnualMin.toLocaleString()} - $${estimatedAnnualMax.toLocaleString()} / yr`;
    bonusOrCommission = `Quarterly team performance bonuses`;
    bonusAvailable = true;
    transparencyScore = 80;
  } else {
    // Default generic categories
    basePayMin = 18.00 + (absHash % 5);
    basePayMax = 25.00 + (absHash % 8);
    basePay = `$${basePayMin.toFixed(2)} - $${basePayMax.toFixed(2)} / hr`;
    estimatedAnnualMin = Math.round(basePayMin * 2000);
    estimatedAnnualMax = Math.round(basePayMax * 2000);
    estimatedTotal = `$${estimatedAnnualMin.toLocaleString()} - $${estimatedAnnualMax.toLocaleString()} / yr`;
  }

  // Healthcare and retirement structure
  const hasMedical = absHash % 3 !== 0;
  const hasDental = absHash % 4 !== 0;
  const hasVision = absHash % 5 !== 0;
  const retirementAvailable = absHash % 4 !== 0;
  const employerMatch = absHash % 6 === 0;
  const pto = absHash % 3 !== 0;

  const compensationSource = absHash % 3 === 0 
    ? "Employer Self-Reported" 
    : (absHash % 3 === 1 ? "Glassdoor Benchmark Estimates" : "Indeed Crawled Vacancy");
  const compConfidence: "high" | "medium" | "low" | "unknown" = absHash % 3 === 0 ? "high" : "medium";
  const compLastChecked = new Date(Date.now() - (absHash % 15) * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  const compConfirmed = absHash % 3 === 0;
  const compEstimated = absHash % 3 === 1;
  const compInferred = absHash % 3 === 2;

  // Workplace Flexibility details
  let locationType: "remote" | "hybrid" | "on-site" = "on-site";
  let workLocationType: "remote" | "hybrid" | "onsite" | "mixed" | "unknown" = "onsite";

  if (cat.includes("marketing") || cat.includes("consult") || cat.includes("design") || cat.includes("software") || cat.includes("tech") || cat.includes("account")) {
    const locIdx = absHash % 3;
    if (locIdx === 0) {
      locationType = "remote";
      workLocationType = "remote";
    } else if (locIdx === 1) {
      locationType = "hybrid";
      workLocationType = "hybrid";
    } else {
      locationType = "on-site";
      workLocationType = "onsite";
    }
  }

  const flexibleHours = absHash % 3 !== 0;
  const partTimeAvailable = absHash % 4 !== 0;
  const fullTimeAvailable = absHash % 5 !== 0;

  const employmentTypes: ("full-time" | "part-time" | "contract" | "internship")[] = ["full-time", "part-time", "contract", "internship"];
  const employmentType = employmentTypes[absHash % employmentTypes.length];
  
  const shiftOptionsList = [
    "Day shift, Monday to Friday",
    "Flexible hours, rotating weekend shifts",
    "Standard business hours (9am - 5pm)",
    "Evening shifts, open weekend availability",
    "Morning and afternoon blocks available"
  ];
  const shiftOptions = shiftOptionsList[absHash % shiftOptionsList.length];

  const flexSource = absHash % 2 === 0 ? "Company Portal" : "Indeed Work Style Survey";
  const flexConfidence: "high" | "medium" | "low" | "unknown" = absHash % 4 === 0 ? "high" : "medium";

  // Job Security details
  const financialHealths: ("stable" | "growing" | "declining" | "undocumented")[] = ["stable", "growing", "undocumented"];
  const financialHealth = financialHealths[absHash % financialHealths.length];
  
  const tenureYrs = parseFloat((1.5 + (absHash % 10) * 0.5).toFixed(1));
  const businessHistoryYrs = 3 + (absHash % 30);
  
  const securityIndicators: ("high" | "medium" | "low" | "neutral")[] = ["high", "medium", "neutral"];
  const securityIndicator = securityIndicators[absHash % securityIndicators.length];

  const employmentDurations: ("permanent" | "temporary" | "seasonal" | "contract" | "unknown")[] = ["permanent", "contract", "unknown"];
  const employmentDuration = employmentDurations[absHash % employmentDurations.length];

  const dbEmploymentTypes: ("full_time" | "part_time" | "contract" | "internship" | "unknown")[] = ["full_time", "part_time", "contract", "unknown"];
  const dbEmploymentType = dbEmploymentTypes[absHash % dbEmploymentTypes.length];

  const stabilityScore = 55 + (absHash % 40);

  const securityIndicatorsList = absHash % 2 === 0 
    ? ["Licensed local operation", "Consistent map review frequency", "Active website status"]
    : ["Longstanding business operations", "Verified location presence"];

  const securityConcernsList = absHash % 5 === 0 
    ? ["Intense local sector competition"]
    : [];

  // Career Growth details
  const trainingAvailable = absHash % 4 !== 0;
  const promotionPotentials: ("high" | "medium" | "low" | "undocumented")[] = ["high", "medium", "undocumented"];
  const promotionPotential = promotionPotentials[absHash % promotionPotentials.length];
  const tuitionSupport = absHash % 5 === 0;
  
  const growthOppsList = [
    "Dedicated training budget with pathways to team-lead positions.",
    "Hands-on mentorship from senior technicians with regular quarterly reviews.",
    "Annual certification reimbursement and clear step-by-step promotion structure.",
    "On-the-job training program designed for career switchers and entry-level talent.",
    "Rapidly expanding local branch with clear opportunities to step into management roles."
  ];
  const growthOpportunities = growthOppsList[absHash % growthOppsList.length];

  const advancementMentioned = absHash % 2 === 0;
  const mentorshipAvailable = absHash % 3 === 0;
  const certificationsSupported = absHash % 4 === 0;
  const internalMobilityMentioned = absHash % 5 === 0;

  const growthIndicators = absHash % 2 === 0
    ? ["Structured learning catalog", "Mentorship pairing"]
    : ["On-the-job micro-credentials"];

  // Company Culture details
  const rating = parseFloat((3.5 + (absHash % 15) * 0.1).toFixed(1));
  
  const feedbackList = [
    "Employees praise the supportive team atmosphere, flexible schedule accommodations, and friendly management style.",
    "Highly collaborative workspace with a focus on work-life balance and open-door leadership communication.",
    "Fast-paced but highly rewarding environment with a strong culture of recognizing and celebrating local achievements.",
    "Strong emphasis on safety, quality craftsmanship, and clear, respectful team synchronization.",
    "Inclusive local culture focused on community engagement, professional growth, and healthy work boundaries."
  ];
  const feedbackSummary = feedbackList[absHash % feedbackList.length];
  
  const workLifeBalances: ("excellent" | "good" | "moderate" | "poor")[] = ["excellent", "good", "moderate"];
  const workLifeBalance = workLifeBalances[absHash % workLifeBalances.length];

  const cultureScore = Math.round(rating * 20);

  const positiveCultureIndicators = absHash % 2 === 0
    ? ["Empathetic leadership", "High transparency", "Strong peer recognition"]
    : ["Active work-life boundary guidelines", "Fun team events"];

  const cautionCultureIndicators = absHash % 6 === 0
    ? ["Fast paced environment can lead to long days"]
    : [];

  const employerStatedValues = absHash % 2 === 0
    ? ["Integrity", "Safety", "Customer Obsession"]
    : ["Quality Craftsmanship", "Inclusivity", "Local Community Support"];

  const cultureReviewCount = 5 + (absHash % 115);

  // Metadata details
  const hiringLikelihood = lead.rating && lead.rating >= 4.0 ? "high" as const : "medium" as const;
  const confidenceScore = 65 + (absHash % 30);
  
  const lastCheckedDaysAgo = 1 + (absHash % 14);
  const lastCheckedDate = `Verified ${lastCheckedDaysAgo} days ago`;

  return {
    compensation: {
      basePay,
      estimatedTotal,
      bonusOrCommission,
      healthcareBenefits: hasMedical,
      retirementBenefits: retirementAvailable,
      pto,
      transparencyScore,
      // Extended fields:
      basePayMin,
      basePayMax,
      payPeriod,
      currency,
      estimatedAnnualMin,
      estimatedAnnualMax,
      bonusAvailable,
      commissionAvailable,
      overtimeAvailable,
      healthcareBenefitsObj: {
        medical: hasMedical,
        dental: hasDental,
        vision: hasVision
      },
      retirementBenefitsObj: {
        available: retirementAvailable,
        employerMatch: employerMatch
      },
      paidTimeOff: pto,
      additionalBenefits: absHash % 2 === 0 ? ["Professional training tools", "Paid certification courses"] : ["Free on-site parking", "Team meals"],
      source: compensationSource,
      confidence: compConfidence,
      lastChecked: compLastChecked,
      isConfirmed: compConfirmed,
      isEstimated: compEstimated,
      isInferred: compInferred
    },
    workplaceFlexibility: {
      locationType,
      flexibleHours,
      employmentType,
      shiftOptions,
      // Extended fields:
      workLocationType,
      partTimeAvailable,
      fullTimeAvailable,
      scheduleOptions: absHash % 2 === 0 ? ["Flexible Shift Options"] : ["Fixed 5-Day Business Schedule"],
      shiftOptionsList: [shiftOptions],
      source: flexSource,
      confidence: flexConfidence,
      lastChecked: compLastChecked,
      isConfirmed: absHash % 3 === 0,
      isEstimated: absHash % 3 === 1,
      isInferred: absHash % 3 === 2
    },
    jobSecurity: {
      financialHealth,
      tenureYrs,
      businessHistoryYrs,
      securityIndicator,
      // Extended fields:
      employmentDuration,
      employmentType: dbEmploymentType,
      stabilityScore,
      indicators: securityIndicatorsList,
      concerns: securityConcernsList,
      source: "Local License Registry & Web Audit",
      confidence: absHash % 3 === 0 ? "high" : "medium",
      lastChecked: compLastChecked,
      isConfirmed: absHash % 2 === 0,
      isEstimated: false,
      isInferred: absHash % 2 !== 0
    },
    careerGrowth: {
      trainingAvailable,
      promotionPotential,
      tuitionSupport,
      growthOpportunities,
      // Extended fields:
      advancementMentioned,
      mentorshipAvailable,
      tuitionAssistance: tuitionSupport,
      certificationsSupported,
      internalMobilityMentioned,
      growthIndicators,
      source: "Company Career Handbook Analysis",
      confidence: absHash % 3 === 0 ? "high" : "medium",
      lastChecked: compLastChecked,
      isConfirmed: absHash % 3 === 0,
      isEstimated: false,
      isInferred: absHash % 3 !== 0
    },
    companyCulture: {
      rating,
      feedbackSummary,
      workLifeBalance,
      // Extended fields:
      cultureScore,
      positiveIndicators: positiveCultureIndicators,
      cautionIndicators: cautionCultureIndicators,
      employerStatedValues,
      employeeFeedbackSummary: feedbackSummary,
      reviewCount: cultureReviewCount,
      sourceTypes: absHash % 2 === 0 ? ["Verified Employer Reviews"] : ["Social Media Sentiment Analyses", "Survey Benchmarks"],
      confidence: absHash % 3 === 0 ? "high" : "medium",
      lastChecked: compLastChecked,
      isConfirmed: false,
      isEstimated: true,
      isInferred: false
    },
    metadata: {
      hiringLikelihood,
      confidenceScore,
      lastCheckedDate
    }
  };
}
